import { useEffect } from "react";
import { useParams, useLocation, Navigate, Link as RouterLink, NavLink as RouterNavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LANGS } from "@/i18n";
import { Layout } from "@/components/layout/Layout";

const STORAGE_KEY = "vc_lang";

export const getSavedLang = () => {
  const saved = typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
  return LANGS.includes(saved) ? saved : "en";
};

export const useLang = () => {
  const { lang } = useParams();
  return LANGS.includes(lang) ? lang : "en";
};

const withLang = (lang, to) => {
  if (!to) return `/${lang}`;
  if (to === "/") return `/${lang}`;
  if (to.startsWith("/")) return `/${lang}${to}`;
  return to; // external / hash
};

/** Language-aware Link — keeps the current locale prefix. */
export const Link = ({ to, ...props }) => {
  const lang = useLang();
  return <RouterLink to={withLang(lang, to)} {...props} />;
};

/** Language-aware NavLink. */
export const NavLink = ({ to, ...props }) => {
  const lang = useLang();
  return <RouterNavLink to={withLang(lang, to)} {...props} />;
};

/** Layout for /:lang — validates locale, sets i18n language, persists it. */
export const LangLayout = () => {
  const { lang } = useParams();
  const location = useLocation();
  const { i18n } = useTranslation();

  const valid = LANGS.includes(lang);

  useEffect(() => {
    if (valid) {
      if (i18n.language !== lang) i18n.changeLanguage(lang);
      localStorage.setItem(STORAGE_KEY, lang);
      document.documentElement.lang = lang;
    }
  }, [lang, valid, i18n]);

  if (!valid) {
    const rest = location.pathname.replace(/^\/[^/]+/, "");
    return <Navigate to={`/${getSavedLang()}${rest}${location.search}`} replace />;
  }
  return <Layout />;
};

/** Top-level redirect for paths without a locale prefix → saved/default locale. */
export const RedirectToLang = () => {
  const location = useLocation();
  const path = location.pathname === "/" ? "" : location.pathname;
  return <Navigate to={`/${getSavedLang()}${path}${location.search}`} replace />;
};
